import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-preact';
import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

// An error is something to read and act on, so it stays longer than a confirmation.
export const TOAST_DURATION_MS = 4000;
export const ERROR_TOAST_DURATION_MS = 8000;
const LEAVE_MS = 300;

export type Toast = {
    id: string;
    variant: ToastVariant;
    title?: string;
    message: string;
    duration?: number;
    /** Skips the auto-dismiss timers — for messages the user must act on, not just notice. */
    persist?: boolean;
};

type ToastContextType = {
    toast: (options: Omit<Toast, 'id'>) => void;
    success: (message: string, title?: string, persist?: boolean) => void;
    error: (message: string, title?: string, persist?: boolean) => void;
    warning: (message: string, title?: string, persist?: boolean) => void;
    info: (message: string, title?: string, persist?: boolean) => void;
};

export const ToastContext = React.createContext<ToastContextType | null>(null);

const STYLES: Record<ToastVariant, {
    container: string; icon: string; title: string; body: string; bar: string; close: string;
    Icon: React.ComponentType<{ className?: string }>;
}> = {
    success: {
        container: 'bg-white border border-emerald-200 shadow-lg shadow-emerald-100/50',
        icon: 'text-emerald-500', title: 'text-gray-900', body: 'text-gray-600',
        bar: 'bg-emerald-400', close: 'text-gray-400 hover:text-gray-600', Icon: CheckCircle,
    },
    error: {
        container: 'bg-white border border-red-200 shadow-lg shadow-red-100/50',
        icon: 'text-red-500', title: 'text-gray-900', body: 'text-gray-600',
        bar: 'bg-red-400', close: 'text-gray-400 hover:text-gray-600', Icon: XCircle,
    },
    warning: {
        container: 'bg-white border border-amber-200 shadow-lg shadow-amber-100/50',
        icon: 'text-amber-500', title: 'text-gray-900', body: 'text-gray-600',
        bar: 'bg-amber-400', close: 'text-gray-400 hover:text-gray-600', Icon: AlertTriangle,
    },
    info: {
        container: 'bg-white border border-blue-200 shadow-lg shadow-blue-100/50',
        icon: 'text-blue-500', title: 'text-gray-900', body: 'text-gray-600',
        bar: 'bg-blue-400', close: 'text-gray-400 hover:text-gray-600', Icon: Info,
    },
};

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
    const [visible, setVisible] = React.useState(false);
    const [leaving, setLeaving] = React.useState(false);
    const [paused, setPaused] = React.useState(false);
    const duration = toast.duration ?? (toast.variant === 'error' ? ERROR_TOAST_DURATION_MS : TOAST_DURATION_MS);
    // The clock only runs while nobody is pointing at the toast: what is left is kept, so letting go resumes it.
    const remaining = React.useRef(duration);
    const startedAt = React.useRef(0);
    const timers = React.useRef<{ leave?: ReturnType<typeof setTimeout>; remove?: ReturnType<typeof setTimeout> }>({});
    const styles = STYLES[toast.variant];
    const { Icon } = styles;

    const startClock = () => {
        startedAt.current = Date.now();
        timers.current.leave = setTimeout(() => {
            setLeaving(true);
            timers.current.remove = setTimeout(() => onRemove(toast.id), LEAVE_MS);
        }, remaining.current);
    };

    React.useEffect(() => {
        const show = setTimeout(() => setVisible(true), 10);
        if (!toast.persist) startClock();
        return () => { clearTimeout(show); clearTimeout(timers.current.leave); clearTimeout(timers.current.remove); };
    }, []);

    const hold = () => {
        if (toast.persist || paused || leaving) return;
        clearTimeout(timers.current.leave);
        remaining.current -= Date.now() - startedAt.current;
        setPaused(true);
    };

    const release = () => {
        if (toast.persist || !paused || leaving) return;
        setPaused(false);
        startClock();
    };

    const dismiss = () => {
        setLeaving(true);
        clearTimeout(timers.current.leave);
        timers.current.remove = setTimeout(() => onRemove(toast.id), LEAVE_MS);
    };

    return (
        <div
            data-testid="toast" data-variant={toast.variant} onMouseEnter={hold} onMouseLeave={release} onFocus={hold} onBlur={release}
            className={`relative w-80 rounded-xl overflow-hidden transition-all duration-300 ease-out ${styles.container} ${visible && !leaving ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
        >
            {!toast.persist && (
                <div className={`toast-progress absolute top-0 left-0 h-0.5 w-full ${styles.bar}`} data-paused={paused ? 'true' : 'false'} style={{ '--toast-duration': `${duration}ms` } as React.CSSProperties} />
            )}
            <div className="flex items-start gap-3 p-4">
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${styles.icon}`} />
                <div className="flex-1 min-w-0">
                    {toast.title && <p className={`text-sm font-semibold mb-0.5 ${styles.title}`}>{toast.title}</p>}
                    <p className={`text-sm leading-relaxed whitespace-pre-line ${styles.body}`}>{toast.message}</p>
                </div>
                <button type="button" onClick={dismiss} className={`shrink-0 mt-0.5 transition-colors ${styles.close}`}>
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = React.useState<Toast[]>([]);

    // Both use the updater form, so they never close over `toasts` and never need to be recreated.
    const add = React.useCallback((options: Omit<Toast, 'id'>) => {
        const id = Math.random().toString(36).slice(2);
        setToasts(prev => [...prev, { ...options, id }]);
    }, []);

    const remove = React.useCallback((id: string) => setToasts(prev => prev.filter(t => t.id !== id)), []);

    /**
     * Memoised because ~53 effects across the app list `toast` as a dependency. A new object here
     * re-runs every one of them on each provider render, which on data-heavy pages means firing the
     * same queries again and again — enough to exhaust the database connection pool.
     */
    const ctx: ToastContextType = React.useMemo(() => ({
        toast: add,
        success: (message, title, persist) => add({ variant: 'success', message, title, persist }),
        error:   (message, title, persist) => add({ variant: 'error',   message, title, persist }),
        warning: (message, title, persist) => add({ variant: 'warning', message, title, persist }),
        info:    (message, title, persist) => add({ variant: 'info',    message, title, persist }),
    }), [add]);

    // Lets non-React code (the app-level error handler in client/index.ts) raise a toast through
    // the same styled system every page already uses, instead of proteum's unstyled legacy dialog.
    React.useEffect(() => {
        activeToastApi = ctx;
        return () => {
            if (activeToastApi === ctx) activeToastApi = null;
        };
    }, [ctx]);

    return (
        <ToastContext.Provider value={ctx}>
            {children}
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 pointer-events-none items-center">
                {toasts.map(t => (
                    <div key={t.id} className="pointer-events-auto">
                        <ToastItem toast={t} onRemove={remove} />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

const noopToast: ToastContextType = {
    toast: () => {},
    success: () => {},
    error: () => {},
    warning: () => {},
    info: () => {},
};

export function useToast() {
    const ctx = React.useContext(ToastContext);
    return ctx ?? noopToast;
}

/**
 * The most recently mounted ToastProvider, for callers outside the React tree. Used by the
 * app-level error handler (client/index.ts), which is a plain class method and cannot use hooks.
 */
let activeToastApi: ToastContextType | null = null;

/** Falls back to a console warning if no provider has mounted yet, rather than throwing. */
export function getGlobalToast(): ToastContextType {
    if (activeToastApi) return activeToastApi;
    console.warn('[toast] No ToastProvider is mounted yet; dropping toast.');
    return noopToast;
}
